/**
 * Vigência da proposta.
 *
 * Mora no domínio porque tem dois consumidores: o renderizador, que decide qual faixa exibir no
 * documento entregue ao cliente, e o painel, que precisa do selo de situação na listagem (Task 12).
 * Duplicar essa regra levaria a painel e documento discordando sobre a mesma proposta.
 *
 * Requisito de negócio: a proposta **permanece acessível para sempre**. O vencimento é sinalização
 * comercial, nunca controle de acesso. No legado a proposta ficava inacessível, o que fazia o cliente
 * perder o material de referência e a eMaster perder o gancho de retomada.
 */

/** Antecedência do aviso de vencimento próximo, em dias. */
export const DIAS_AVISO_VENCIMENTO = 7;

export type EstadoVigencia = 'vigente' | 'vence_em_breve' | 'vencida';

export interface Vigencia {
  readonly estado: EstadoVigencia;
  /**
   * Dias inteiros entre hoje e a validade. Negativo quando já venceu, zero no próprio dia.
   * Contado em UTC, para não variar conforme o fuso de quem abre a proposta.
   */
  readonly diasRestantes: number;
}

const MS_POR_DIA = 86_400_000;

/** Converte `AAAA-MM-DD` em milissegundos UTC à meia-noite. */
function meiaNoiteUtc(dataSimples: string): number {
  const [ano, mes, dia] = dataSimples.split('-').map(Number) as [number, number, number];
  return Date.UTC(ano, mes - 1, dia);
}

/**
 * Calcula a vigência comparando apenas a data, sem hora.
 *
 * A comparação é em UTC de propósito: uma proposta válida até 03/09 não deve aparecer como vencida
 * para quem está em fuso adiantado nem como vigente para quem está atrasado. O que vale é o dia
 * declarado no documento.
 *
 * @param validaAte Data de validade em `AAAA-MM-DD`.
 * @param hoje Data de referência em `AAAA-MM-DD`. Injetada para permitir teste determinístico.
 */
export function calcularVigencia(validaAte: string, hoje: string): Vigencia {
  const diasRestantes = Math.round((meiaNoiteUtc(validaAte) - meiaNoiteUtc(hoje)) / MS_POR_DIA);

  if (diasRestantes < 0) return { estado: 'vencida', diasRestantes };
  if (diasRestantes <= DIAS_AVISO_VENCIMENTO) return { estado: 'vence_em_breve', diasRestantes };
  return { estado: 'vigente', diasRestantes };
}

/** Texto de apoio para o estado, em linguagem voltada ao cliente. */
export function descreverVigencia(v: Vigencia): string {
  if (v.estado === 'vencida') {
    const dias = Math.abs(v.diasRestantes);
    return dias === 1 ? 'Venceu ontem.' : `Venceu há ${dias} dias.`;
  }
  if (v.diasRestantes === 0) return 'Vence hoje.';
  if (v.diasRestantes === 1) return 'Vence amanhã.';
  return `Vence em ${v.diasRestantes} dias.`;
}
